import { alpha, Box, Grid, TextField, Typography } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { usePayrollPlatforms } from './usePayrollPlatforms'

// Form data interface for referral fields
export interface ReferralFormFields {
  referralPath: string
  // Word of mouth fields
  wordOfMouthFirst: string
  wordOfMouthLast: string
  wordOfMouthCompany: string
  wordOfMouthEmail: string
  // Broker/consultant fields
  brokerEmail: string
  brokerName: string
  partnerExecutiveId: string
  // Other referral
  otherNote: string
}

interface ReferralSlideProps<T extends ReferralFormFields> {
  register: UseFormRegister<T>
  errors: FieldErrors<T>
  watch: <K extends keyof T>(name: K) => T[K]
  payrollPlatform?: string
}

export const ReferralInputs = <T extends ReferralFormFields>({
  register,
  errors,
  watch,
  payrollPlatform,
}: ReferralSlideProps<T>) => {
  const { data: payrollPlatforms, isLoading: payrollPlatformsLoading } = usePayrollPlatforms()
  const referralPath = watch('referralPath' as keyof T) as string
  const wordOfMouthEmail = watch('wordOfMouthEmail' as keyof T) as string
  const brokerEmail = watch('brokerEmail' as keyof T) as string
  const partnerExecutiveId = watch('partnerExecutiveId' as keyof T) as string

  // Email validation helper
  const isValidEmail = (email: string) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
  }

  // Primary dropdown options
  const getPrimaryDropdownOptions = () => [
    'Word of mouth',
    'Google search',
    'LinkedIn',
    'Industry publication',
    'Conference/event',
    'Broker/consultant',
    'Other',
  ]

  return (
    <>
      <Grid container spacing={3} width="100%">
        {/* Primary dropdown */}
        <Grid size={12}>
          <TextField
            {...register('referralPath' as never, { required: 'Please select how you heard about Alice' })}
            label="How did you hear about Alice?"
            fullWidth
            select
            error={!!errors.referralPath}
            helperText={errors.referralPath?.message as string}
            defaultValue=""
          >
            <option value="" disabled>
              Select an option
            </option>
            {getPrimaryDropdownOptions().map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </TextField>
        </Grid>

        {/* Word of mouth follow-up */}
        {referralPath === 'Word of mouth' && (
          <Grid size={12}>
            <Box p={3} sx={{ backgroundColor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                Tell us who referred you to Alice. We&apos;ll send them a thank-you gift once you launch!
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register('wordOfMouthFirst' as never, {
                      required: referralPath === 'Word of mouth' ? 'First name is required' : false,
                    })}
                    label="First Name *"
                    fullWidth
                    error={!!errors.wordOfMouthFirst}
                    helperText={errors.wordOfMouthFirst?.message as string}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register('wordOfMouthLast' as never, {
                      required: referralPath === 'Word of mouth' ? 'Last name is required' : false,
                    })}
                    label="Last Name *"
                    fullWidth
                    error={!!errors.wordOfMouthLast}
                    helperText={errors.wordOfMouthLast?.message as string}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    {...register('wordOfMouthCompany' as never, {
                      required: referralPath === 'Word of mouth' ? 'Company is required' : false,
                    })}
                    label="Company *"
                    fullWidth
                    error={!!errors.wordOfMouthCompany}
                    helperText={errors.wordOfMouthCompany?.message as string}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    {...register('wordOfMouthEmail' as never, {
                      required: referralPath === 'Word of mouth' ? 'Email is required' : false,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address',
                      },
                    })}
                    label="Email *"
                    type="email"
                    fullWidth
                    error={
                      !!errors.wordOfMouthEmail ||
                      Boolean(wordOfMouthEmail && !isValidEmail(wordOfMouthEmail))
                    }
                    helperText={
                      (errors.wordOfMouthEmail?.message as string) ||
                      (wordOfMouthEmail && !isValidEmail(wordOfMouthEmail)
                        ? 'Please enter a valid email address'
                        : '')
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Broker/consultant follow-up */}
        {referralPath === 'Broker/consultant' && (
          <Grid size={12}>
            <Box
              sx={(theme) => ({
                p: 2,
                width: '100%',
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderRadius: 2,
              })}
            >
              <Typography variant="body2" gutterBottom>
                Let us know who your broker/consultant is so that we can add them as a viewer on your
                company&apos;s Alice account and send them a thank-you gift!
              </Typography>
              <Grid container spacing={2}>
                {(() => {
                  const partnerExecutives =
                    payrollPlatforms?.find((platform) => platform.code === payrollPlatform)
                      ?.partnerExecutives || []

                  // If no partner executives available, show name and email inputs directly
                  if (partnerExecutives.length === 0) {
                    return (
                      <>
                        <Grid size={12}>
                          <TextField
                            {...register('brokerName' as never, {
                              required: referralPath === 'Broker/consultant' ? 'Name is required' : false,
                            })}
                            label="Name *"
                            fullWidth
                            error={!!errors.brokerName}
                            helperText={errors.brokerName?.message as string}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            {...register('brokerEmail' as never, {
                              required: referralPath === 'Broker/consultant' ? 'Email is required' : false,
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Please enter a valid email address',
                              },
                            })}
                            label="Email *"
                            type="email"
                            fullWidth
                            error={!!errors.brokerEmail || Boolean(brokerEmail && !isValidEmail(brokerEmail))}
                            helperText={
                              (errors.brokerEmail?.message as string) ||
                              (brokerEmail && !isValidEmail(brokerEmail)
                                ? 'Please enter a valid email address'
                                : '')
                            }
                          />
                        </Grid>
                      </>
                    )
                  }

                  // If partner executives are available, show the select dropdown
                  return (
                    <>
                      <Grid size={12}>
                        <TextField
                          {...register('partnerExecutiveId' as never, {
                            required:
                              referralPath === 'Broker/consultant'
                                ? 'Please select a broker/consultant'
                                : false,
                          })}
                          label="Broker/consultant *"
                          fullWidth
                          select
                          error={!!errors.partnerExecutiveId}
                          helperText={errors.partnerExecutiveId?.message as string}
                          defaultValue=""
                          disabled={payrollPlatformsLoading}
                        >
                          <option value="" disabled>
                            {payrollPlatformsLoading ? 'Loading...' : 'Select a broker/consultant'}
                          </option>
                          {partnerExecutives.map((executive) => (
                            <option key={executive.id} value={executive.id.toString()}>
                              {executive.firstName} {executive.lastName}
                            </option>
                          ))}
                          <option value="Other">Other</option>
                        </TextField>
                      </Grid>

                      {/* Show name and email inputs only when "Other" is selected */}
                      {partnerExecutiveId === 'Other' && (
                        <>
                          <Grid size={12}>
                            <TextField
                              {...register('brokerName' as never, {
                                required: partnerExecutiveId === 'Other' ? 'Name is required' : false,
                              })}
                              label="Name *"
                              fullWidth
                              error={!!errors.brokerName}
                              helperText={errors.brokerName?.message as string}
                            />
                          </Grid>
                          <Grid size={12}>
                            <TextField
                              {...register('brokerEmail' as never, {
                                required: partnerExecutiveId === 'Other' ? 'Email is required' : false,
                                pattern: {
                                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: 'Please enter a valid email address',
                                },
                              })}
                              label="Email *"
                              type="email"
                              fullWidth
                              error={
                                !!errors.brokerEmail || Boolean(brokerEmail && !isValidEmail(brokerEmail))
                              }
                              helperText={
                                (errors.brokerEmail?.message as string) ||
                                (brokerEmail && !isValidEmail(brokerEmail)
                                  ? 'Please enter a valid email address'
                                  : '')
                              }
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )
                })()}
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Other follow-up */}
        {referralPath === 'Other' && (
          <Grid size={12}>
            <Box p={3} sx={{ backgroundColor: 'grey.50', borderRadius: 2 }}>
              <TextField
                {...register('otherNote' as never, {
                  required: referralPath === 'Other' ? 'Please provide details' : false,
                })}
                label="Please provide details *"
                fullWidth
                multiline
                rows={3}
                error={!!errors.otherNote}
                helperText={errors.otherNote?.message as string}
                placeholder="Please describe how you heard about Alice"
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  )
}
